/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isString from 'lodash.isstring';
// eslint-disable-next-line no-restricted-imports
import {NextRouter} from 'next/router';
import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
import {savePassword} from '@ion/applications/src/pages/admin/users/state';
import {setSessionExpired} from '@ion/applications/src/preferences/UserPreferencesAutoSave';
import {
  deleteLocalStorage,
  localPersistence
} from '@ion/core/src/capabilities/storage';
import {get} from '@ion/core/src/environment/environment';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getRole} from '@ion/core/src/security/helpers';
import {
  authenticate,
  getAccount,
  logout
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getSessionToken, invalidateSessionToken} from '@ion/core/src/services/CTCAuthToken';
import Sisense from '@ion/core/src/services/Sisense';
import {setUserTimeZone} from '@ion/core/src/timezone/timezone';
import {User} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {setUserUnit} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {encode} from '@ion/core/src/utils/Cipher';
import {isSame} from '@ion/core/src/utils/DateUtil';
import {hydrateCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {setAuthToken as setGraphQLAuthToken} from '@ion/services/src/graphql/GraphQLConfig';
import {currentUserQuery} from '@ion/services/src/graphql/QueryAdapter';
import {ApplicationKeys} from '@/types/application';
import {isValidErrorCode} from '@/utils/login-utils';
import changePage, {
  getRoute,
  Route
} from '../../routes/RouteDefinitions';
import {storePreviousPage} from './helpers';
import {
  APPLICATION_INITIALIZATION_ERROR,
  initialize,
  metricsBegin,
  metricsEnd,
  preinitialization
} from './initialization';
import {setPreviousPage} from './nav';
import {toggleUserPrompt} from './user-prompt';

export const LOGIN_REQUEST       = 'LOGIN_REQUEST';
export const LOGIN_SUCCEEDED     = 'LOGIN_SUCCEEDED';
export const LOGIN_FAILED        = 'LOGIN_FAILED';
export const PERSIST_CREDENTIALS = 'PERSIST_CREDENTIALS';
export const LOGOUT_REQUEST      = 'LOGOUT_REQUEST';
export const LOGOUT_FAILED       = 'LOGOUT_FAILED';
export const UPDATE_CURRENT_USER = 'UPDATE_CURRENT_USER';
export const SOLOMON_ID          = 'SOLOMON_ID';

/**
 * Provides a pre-authentication check which first attempts to retrieve the current
 * user if the user has an session timestamp persisted that is within the same day
 * (CTC timeouts are based on a one hour of no activity interval; however, we retry
 * for the duration of a day as the session may continue to be renewed). If returned
 * successfully confirms that the users current session is still active, otherwise,
 * if the call fails, returns null and the user is redirected to the login page.
 */
const preauth = async(isReferrer: boolean) => {
  const session = localPersistence(ApplicationKeys.CTCAdminSession, 'session');
  try {
    if (isReferrer || (session && isSame(session, new Date(), 'day'))) {
      if (get('oidcEnabled')) {
        const accessToken = await authMgr.getAccessToken();
        if (accessToken) {
          setGraphQLAuthToken(accessToken.accessToken);
          await authMgr.updateHeaders(accessToken);
        }
      } else {
        const token = getSessionToken();
        setGraphQLAuthToken(token);
      }
      return await currentUserQuery();
    }
  } catch (err) {
    error(`An Exception occurred while performing Application pre-authorization, ${err.toString()}`);
  }
  return null;
};

/**
 * @login
 *
 * The login function provides the single point of entry for authenticating with CTC
 * Services. If invoked without a username and password, the application will simply
 * execute the preauth phase which will verify if the user has an active sessions,
 * otherwise, if invoked with a username and password, the preauth phase will still
 * be verified and if the session is invalid the user will be authenticated with the
 * given credentials.
 *
 * The ADM Login Process is based on the following flow of control:
 *
 *   1. If invoked with a username and password, dispatch a LOGIN_REQUEST
 *   2. Determine if the app has OIDC Enabled
 *   3. If there is already an active session, retrieve the current user.
 *   4. If retrieved, dispatch loginComplete action which subsequently Hydrates
 *      and initialized specific APIs with a reference to the current user, as
 *      well as dispatches an initialize action once authentication has been
 *      completed.
 *   5. If not, and a username and password is provided:
 *      - If OIDC is Enabled, authenticate the user via their IDP redirect.
 *      - Otherwise, authenticate the user via CTC, and retrieve the current user
 *        entity via GraphQL Services, which cast the User to a CTC User entity.
 *   6. If there is not an active session, and a username and password has not been
 *      provided, and OIDC is not Enabled, re-direct the user to the login Page.
 *
 */
export const login = (username?: string, password?: string, router?: NextRouter) => {
  metricsBegin();
  return async(dispatch, getState) => {
    if (username && password) {
      clearSessionToken();
      dispatch({type: LOGIN_REQUEST});
    }
    const isFromLegacyAdmin = Boolean(router?.query?.referrer === 'legacy-admin');
    const oidcEnabled = get('oidcEnabled');
    const authParams  = {oidcEnabled: oidcEnabled};
    let user = await preauth(isFromLegacyAdmin);
    try {
      if (!user && username && password) {
        const persist = getState().login.persistCredentials;
        persistUser(persist, username, password);
        if (oidcEnabled) {
          authMgr.oidcLogin(username);
        } else {
          user = await loginCTC(username, password);
          dispatch(savePassword(password));
        }
      } else if (!user) {
        // If we are redirecting user to the login page
        // but they landed on a non-login page, dispatch that
        // as the previous page so we can re-route them
        // there after login.
        if (router?.route !== getRoute(Route.LOGIN)) {
          oidcEnabled && storePreviousPage(router?.route, JSON.stringify(authParams));
          dispatch(setPreviousPage(router?.route));
        }
        changePage(Route.LOGIN);
        return false;
      } else {
        oidcEnabled && storePreviousPage(router?.route, JSON.stringify(authParams));
      }
      dispatch(loginComplete(user, router));
    } catch (error) {
      if (oidcEnabled || !error?.isUnauthorizedError) {
        dispatch(toggleUserPrompt({
          message     : 'notifications:error.client',
          actionLabel : 'notifications:warning.actionLabel',
          autoHide    : false
        }));
      }
      metricsEnd();

      dispatch({type: LOGIN_FAILED, error: (error as ServiceError)});
      if (oidcEnabled) {
        const isForbidden = (error as ServiceError).isPermissionsError;
        dispatch({type: APPLICATION_INITIALIZATION_ERROR, isForbidden});
        authMgr.loginFail(isForbidden);
      }
    }
  };
};

/**
 * Provides an abstraction of the CTC login / authorization process when an IDP is not
 * in use. This function facilitates authentication via the native CTC authentication
 * process and sets the GraphQL Auth Token upon completion, retrieving the Current User
 * from GraphQL Services and returning the user cast to a CTC User type.
 */
const loginCTC = async(username, password): Promise<User> => {
  await authenticate(username, password);
  setGraphQLAuthToken(getSessionToken());
  return await currentUserQuery();
};

/**
 * Defines the loginSSO function.
 *
 * This is used when OIDC (OpenID Connect) is enabled and the
 * user is authenticated via an Identity Provider (IDP) and provides the single
 * point of entry and authentication for the CTC Admin application.
 *
 * @param {string} [username]   - The username of the user to be authenticated.
 * @param {string} [password]   - The password of the user to be authenticated.
 * @param {NextRouter} [router] - The Next.js router instance.
 *
 * ```typescript
 *
 * import {loginSSO} from '@ion/core/src/actions';
 *
 * dispatch(loginSSO('tonydanza@vzconnect.com', 'whostheb055?', router));
 *
 * ```
 */
export const loginSSO = (username?: string, password?: string, router?: NextRouter) => {
  metricsBegin();
  return async(dispatch, getState) => {
    (username && password) && dispatch({type: LOGIN_REQUEST});
    const isFromLegacyAdmin = false;
    let user = await preauth(isFromLegacyAdmin);
    if (!username || !password) {
      user = await authMgr.preauth(isFromLegacyAdmin, true);
    }
    const authParams = {oidcEnabled: get('oidcEnabled')};
    try {
      if (!user && username && password) {
        dispatch({type: LOGIN_REQUEST});
        const currUser = await authMgr.ctcLogin(username, password, true, true);
        dispatch(loginComplete(currUser, router));
      } else if (!user) {
        // If we are redirecting user to the login page
        // but they landed on a non-login page, dispatch that
        // as the previous page so we can re-route them
        // there after login.
        authMgr.preauthFail();
        if (router?.route !== getRoute(Route.LOGIN)) {
          storePreviousPage(router?.route, JSON.stringify(authParams));
          dispatch(setPreviousPage(router?.route));
        }
        changePage(Route.LOGIN);
      } else {
        storePreviousPage(router?.route, JSON.stringify(authParams));
        dispatch(loginComplete(user, router));
      }
    } catch (error) {
      dispatch(toggleUserPrompt({
        message     : 'notifications:error.client',
        actionLabel : 'notifications:warning.actionLabel',
        autoHide    : false
      }));
      authMgr.loginFail();
    }
  };
};

/**
 * The `loginComplete` (previously `loginComplete`) login action is dispatched post
 * login but prior to beginning the initialization process, whereby all preinitialization
 * APIs, such as hydration of the current user within the React Query Cache, initializing
 * the System of Units and TimeZone APIs, etc. Once these processes have been completed,
 * the `initialize` action is dispatched and the `initialization` process is kicked off.
 *
 * This action is dispatched from the `login` and `loginSSO` actions once completed.
 *
 * @param  currentUser A reference to the current `User` entity retrieved from GraphQL
 *         Services. The `User` entity, while retrieved from the API-Tier, will be cast
 *         to a CTC `User` type for backwards compatibility.
 * @param  router A reference to the Next JS Application Router from which any page
 *         redirects that are required will be facilitated.
 */
export const loginComplete = (currentUser: User, router: NextRouter) => {
  return async(dispatch, getState) => {
    const oidcEnabled = get('oidcEnabled');
    try {
      if (oidcEnabled) {
        const token = await authMgr.getToken();
        setGraphQLAuthToken(token);
      }
      const user = currentUser || await currentUserQuery();
      verifyADMRole(user);
      const unit = (user?.systemOfUnits?.value || user?.systemOfUnits) as Unit;
      const account = user.enriched?.account || await getAccount(entityId(user.account));
      hydrateCurrentUser(user);
      setUserUnit(unit);
      setUserTimeZone(user?.timeZone);
      Sisense.setAccountId(account.id);
      Sisense.updateSettings({
        unit_of_measurement : unit
      });
      dispatch({type: LOGIN_SUCCEEDED, user, account});
      if (!getState().initialization.preInitializationComplete) {
        dispatch(preinitialization());
      }
      dispatch(initialize(user, router, false));
    } catch (error) {
      handleError(dispatch, error);
      metricsEnd();
      const isForbidden = (error as ServiceError).isPermissionsError;
      dispatch({type: APPLICATION_INITIALIZATION_ERROR, isForbidden});
      oidcEnabled && authMgr.loginFail(isForbidden);
    }
  };
};

/**
 * Provides a convenience function which determines if the authenticated user has
 * an ADM Role assigned and, if not, throws a permissions error for subsequent
 * display to the User.
 */
const verifyADMRole = (user: User) => {
  const hasADMRole = !!getRole(user?.enriched?.roles, get('appKey'));
  if (!hasADMRole) {
    throw {isPermissionsError: true, isUnauthorizedError: true};
  }
};

/**
 * @signoutCTC
 *
 * The signoutCTC function logs the user out of CTC, effectively ending their
 * session, followed by reloading and redirecting them back to the login page.
 */
export const signoutCTC = () => {
  deleteLocalStorage(ApplicationKeys.CTCAdminPage);
  clearSessionToken();
  return async dispatch => {
    try {
      dispatch({type: LOGOUT_REQUEST});
      if (UserPreferences.hasChanges(true)) {
        await UserPreferences.save();
      }
      await Sisense.clearSession();
      await logout();
    } catch (error) {
    } finally {
      location.assign(getRoute(Route.LOGIN));
    }
  };
};

/**
 * signoutSSO Logs the user out, effectively ending their session, followed by
 * reloading and redirecting them back to the login page. This version of the function
 * is used when OIDC (OpenID Connect) is enabled and the user is authenticated via an
 * Identity Provider (IDP).
 *
 * @param {string} username           - The username of the user to be logged out.
 * @param {boolean} [isIdpUser=false] - Optional. Indicates whether the user is authenticated
 *                                      via an IDP. Defaults to false.
 *
 * ```typescript
 *
 * import {signoutSSO} from '@ion/core/src/actions';
 *
 * dispatch(signoutSSO('username', true));
 *
 * ```
 */
export const signoutSSO = (username: string, isIdpUser: boolean = false) => {
  deleteLocalStorage(ApplicationKeys.CTCAdminPage);
  clearSessionToken();
  const oidcEnabled = authMgr.getIsOIDCEnabled();
  const includeArgs = get('_oidcEnabled') === false && oidcEnabled === true;
  return async(dispatch) => {
    try {
      dispatch({type: LOGOUT_REQUEST});
      if (UserPreferences.hasChanges(true)) {
        await UserPreferences.save();
      }
      await Sisense.clearSession();
      if (oidcEnabled && isIdpUser) {
        await authMgr.oidcLogout(username);
      } else {
        await logout();
      }
    } catch (error) {
    } finally {
      authMgr.signoutCleanup();
      if (!oidcEnabled || !isIdpUser) {
        const loginRoute = getRoute(Route.LOGIN);
        location.assign(includeArgs ? `${loginRoute}${window?.location?.search}` : loginRoute);
      }
    }
  };
};

/**
 * @persist
 *
 * Invoked when the user chooses to persist their login credentials.
 */
export const persistCredentials = (persist: boolean) => ({
  type : PERSIST_CREDENTIALS, persist
});

/**
 * Persists the authenticated user's credentials and remember me settings to local
 * storage.
 */
const persistUser = (persist: boolean, username: string, password: string) => {
  if (persist) {
    localPersistence(ApplicationKeys.CTCAdmin, 'login', {
      credentials : {username, password: encode(password)},
      rememberMe  : true
    });
  } else {
    deleteLocalStorage(ApplicationKeys.CTCAdmin);
  }
  localPersistence(ApplicationKeys.CTCAdminSession, 'session', new Date().getTime());
};

/**
 * Invoked when the user's CTC session has expired.
 */
export const sessionTimeout = () => {
  if (UserPreferences.hasChanges(true)) {
    setSessionExpired();
  }
  return () => {
    clearSessionToken();
    location.assign(getRoute(Route.LOGIN));
  };
};

/**
 * Provides a utility which ensures the current Authorization Session Token is removed
 * from localStorage.
 */
const clearSessionToken = () => {
  deleteLocalStorage(ApplicationKeys.CTCAdminSession);
  invalidateSessionToken();
};

/**
 * Defines the handleError function which is responsible for handling errors that
 * occur during the login process.
 */
const handleError = (dispatch, error) => {
  dispatch({type: LOGIN_FAILED, error: error as ServiceError});
  clearSessionToken();
  const errorCode = error?.errorCode;
  if (!isValidErrorCode(errorCode)) {
    const errorMessage = error?.message?.data;
    if (!error?.isUnauthorizedError) {
      dispatch(toggleUserPrompt({
        message     : isString(errorMessage) ? errorMessage : 'notifications:error.client',
        actionLabel : 'notifications:warning.actionLabel',
        autoHide    : false
      }));
    }
  }
};
